import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { trackAddToCart, trackRemoveFromCart } from '../utils/analytics';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  duration: string;
  provider?: string;
  image: string;
  quantity: number;
  date: string;
  time: string;
  category?: string; // Service category/tag (e.g., "Plumbing", "Electrical")
  cartItemId?: string; // Unique identifier for this specific cart item (with its addons combination)
  addons?: {
    id: string;
    name: string;
    price: number;
    duration?: string;
  }[];
}

export interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  cartCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  // Cart state - starts empty, loads from Firestore when user logs in
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoadingFromFirestore, setIsLoadingFromFirestore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const skipNextSave = useRef(false); // Track if we should skip the next save
  const saveTimeoutRef = useRef<number | null>(null); // Debounce timer
  const lastSavedVersion = useRef<string>(''); // Track last saved version to prevent loops
  const lastUserId = useRef<string | null>(null); // Track last logged in user
  const { user } = useAuth();

  // Load cart from Firestore when user logs in (manual read instead of onSnapshot)
  useEffect(() => {
    if (!user) {
      // User logged out - keep cart in state for UI
      // but mark as not initialized so it loads fresh on next login
      console.log('[CartContext] User logged out - preserving cart display');
      if (lastUserId.current) {
        // Only clear if we had a user before (actual logout, not initial load)
        setIsInitialized(false);
      } else {
        // Initial load, no user yet
        setIsInitialized(true);
      }
      lastUserId.current = null;
      return;
    }

    // Check if this is a different user logging in
    if (lastUserId.current && lastUserId.current !== user.uid) {
      // Different user - clear the cart before loading new user's cart
      console.log('[CartContext] Different user logging in - clearing cart');
      setCartItems([]);
    }
    
    lastUserId.current = user.uid;

    // User is logged in, fetch from Firestore
    const fetchCart = async () => {
      const cartDocRef = doc(db, 'carts', user.uid);
      
      try {
        const docSnap = await getDoc(cartDocRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const firestoreCart = data.items || [];
          const firestoreVersion = data.updatedAt || '';
          
          // Check if this is a new version (prevent processing same data twice)
          if (firestoreVersion === lastSavedVersion.current) {
            setIsInitialized(true);
            return;
          }
          
          setIsLoadingFromFirestore(true);
          skipNextSave.current = true; // Skip the save that will be triggered by setCartItems
          lastSavedVersion.current = firestoreVersion; // Update last seen version
          setCartItems(firestoreCart);
          
          setTimeout(() => {
            setIsLoadingFromFirestore(false);
            setIsInitialized(true);
          }, 100);
        } else {
          // No cart in Firestore yet, create empty cart for this user
          try {
            const timestamp = new Date().toISOString();
            lastSavedVersion.current = timestamp;
            await setDoc(cartDocRef, {
              userId: user.uid,
              items: [],
              updatedAt: timestamp
            });
            setCartItems([]);
          } catch (err) {
            // Silently handle - cart will be created when user adds items
          }
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('[CartContext] Error loading cart:', error);
        setIsLoadingFromFirestore(false);
        setIsInitialized(true);
      }
    };
    
    // Initial fetch
    fetchCart();
    
    // Optional: Periodic refresh every 30 seconds (much cheaper than onSnapshot)
    const refreshInterval = setInterval(() => {
      if (user && isInitialized && !isLoadingFromFirestore) {
        fetchCart();
      }
    }, 30000); // 30 seconds

    return () => {
      clearInterval(refreshInterval);
      // Clear any pending save timers
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [user]);

  // Save cart to Firestore whenever it changes (if user is logged in) with debouncing
  useEffect(() => {
    // Check if we should skip this save (after loading from Firestore)
    if (skipNextSave.current) {
      skipNextSave.current = false; // Reset the flag
      return;
    }

    // Skip if not initialized yet (avoid saving before cart is loaded)
    if (!isInitialized) {
      return;
    }

    // Skip if we're currently loading from Firestore (avoid write-back loop)
    if (isLoadingFromFirestore) {
      return;
    }

    if (!user) {
      // Not logged in, don't save anything
      return;
    }

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // User is logged in, save to Firestore with debounce (300ms delay)
    saveTimeoutRef.current = window.setTimeout(async () => {
      try {
        const cartDocRef = doc(db, 'carts', user.uid);
        const timestamp = new Date().toISOString();
        
        // Update last saved version BEFORE saving to prevent processing our own write
        lastSavedVersion.current = timestamp;
        
        await setDoc(cartDocRef, {
          userId: user.uid,
          items: cartItems,
          updatedAt: timestamp
        }, { merge: true }); // Use merge to avoid overwriting other fields
      } catch (err) {
        // Silently handle - will retry on next change
        lastSavedVersion.current = '';
      }
    }, 300); // 300ms debounce

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [cartItems, user, isLoadingFromFirestore, isInitialized]);

  // Helper function to generate a unique cart item ID based on service + addons + date + time
  const generateCartItemId = (item: CartItem): string => {
    const addonIds = (item.addons || []).map(a => a.id).sort().join('-');
    return `${item.id}_${item.date}_${item.time}_${addonIds}`;
  };

  // Helper function to check if two items are identical (same service, addons, date, time)
  const areItemsIdentical = (item1: CartItem, item2: CartItem): boolean => {
    return item1.cartItemId === item2.cartItemId || 
           generateCartItemId(item1) === generateCartItemId(item2);
  };

  // Helper function to check if appointment date/time has passed
  const isAppointmentExpired = (dateStr: string, timeStr: string): boolean => {
    try {
      // Parse the date (format: "YYYY-MM-DD" or similar)
      const appointmentDate = new Date(dateStr);
      
      // Parse the time (format: "HH:MM" or "HH:MM AM/PM")
      const timeParts = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (!timeParts) {
        console.warn('[CartContext] Invalid time format:', timeStr);
        return false; // Don't remove if we can't parse
      }
      
      let hours = parseInt(timeParts[1], 10);
      const minutes = parseInt(timeParts[2], 10);
      const meridiem = timeParts[3]?.toUpperCase();
      
      // Convert to 24-hour format if AM/PM is present
      if (meridiem === 'PM' && hours !== 12) {
        hours += 12;
      } else if (meridiem === 'AM' && hours === 12) {
        hours = 0;
      }
      
      appointmentDate.setHours(hours, minutes, 0, 0);
      
      // Check if appointment time has passed
      const now = new Date();
      return appointmentDate < now;
    } catch (error) {
      console.error('[CartContext] Error checking expiration:', error);
      return false; // Don't remove if there's an error
    }
  };

  // Auto-remove expired cart items periodically
  useEffect(() => {
    if (!isInitialized || cartItems.length === 0) {
      return;
    }

    // Check for expired items immediately
    const checkExpiredItems = () => {
      const expiredItems = cartItems.filter(item => 
        isAppointmentExpired(item.date, item.time)
      );

      if (expiredItems.length > 0) {
        setCartItems(prev => 
          prev.filter(item => !isAppointmentExpired(item.date, item.time))
        );
      }
    };

    // Check immediately
    checkExpiredItems();

    // Then check every minute
    const intervalId = setInterval(checkExpiredItems, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [cartItems, isInitialized]);

  const addToCart = (item: CartItem) => {
    
    // Generate unique cart item ID
    const cartItemId = generateCartItemId(item);
    const itemWithId = { ...item, cartItemId };
    
    setCartItems(prev => {
      // Find an identical item (same service, same addons, same date/time)
      const existingItemIndex = prev.findIndex(i => areItemsIdentical(i, itemWithId));
      
      if (existingItemIndex >= 0) {
        // Identical item found - just increase quantity
        const updatedItems = [...prev];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + item.quantity
        };
        return updatedItems;
      }
      
      // No identical item found - add as new item with unique ID
      const newCart = [...prev, itemWithId];
      
      // Track add to cart
      trackAddToCart({
        id: item.id,
        name: item.name,
        price: item.price,
        category: item.category
      });
      
      return newCart;
    });
  };

  const removeFromCart = (cartItemId: string) => {
    
    // Find the item before removing for analytics
    const itemToRemove = cartItems.find(item => item.cartItemId === cartItemId);
    if (itemToRemove) {
      trackRemoveFromCart({
        id: itemToRemove.id,
        name: itemToRemove.name,
        price: itemToRemove.price
      });
    }
    
    setCartItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    setCartItems(prev => 
      prev.map(item => 
        item.cartItemId === cartItemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      cartCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
